<template>
    <div id="clover-settings"
         class="modl-tab-body">
        <div class="modl-body">
            <div v-if="configError"
                 class="alert alert-danger">
                <p>{{ configError }}</p>
            </div>
            <!-- Having the company level config is a prerequisite. -->
            <form v-if="companyCloverConfig"
                  class="form-h"
                  @submit.prevent="submit">
                <div class="form-group">
                    <label for="clover-terminal-id"
                           class="control-label">{{ l('CLOVER_SETTINGS.X_CLOVER_DEVICE_ID') }}</label>
                    <div class="form-h__right">
                        <div class="input-group-flex">
                            <input id="clover-terminal-id"
                                   v-model="deviceCloverConfig.xCloverDeviceId"
                                   type="text"
                                   disabled="disabled"
                                   class="form-control input-lg">
                            <button class="btn btn-info"
                                    @click.prevent="showTerminals()">
                                {{ l('CLOVER_SETTINGS.VIEW_TERMINALS') }}
                            </button>
                        </div>

                        <p class="help-block">
                            {{ l('CLOVER_SETTINGS.TERMINALS_HELP_UID') }}
                        </p>
                    </div>
                </div>

                <div class="form-group">
                    <label for="clover-base-url"
                           class="control-label">
                        {{ l('CLOVER_SETTINGS.BASE_URL') }}
                    </label>
                    <div class="form-h__right fixedwidth">
                        <input id="clover-base-url"
                               v-model="companyCloverConfig.baseUrl"
                               type="text"
                               disabled="disabled"
                               class="form-control input-lg">
                        <p class="help-block">
                            {{ l('CLOVER_SETTINGS.COMPANY_WIDE_SETTING') }}
                        </p>
                    </div>
                </div>

                <!-- We may want to make this configurable at a later time -->
                <!-- <div class="form-group">
                    <label for="clover-x-clover-timeout"
                           class="control-label">
                        {{ l('CLOVER_SETTINGS.TIMEOUT') }}
                    </label>
                    <div class="form-h__right fixedwidth">
                        <input id="clover-x-clover-timeout"
                               v-model="deviceCloverConfig.xCloverTimeout"
                               type="number"
                               min="0"
                               max="300"
                               class="form-control input-lg">
                        <PaymentConfigDefaultValue config-key="xCloverTimeout"
                                                   :device-config="deviceCloverConfig"
                                                   :company-config="companyCloverConfig" />
                    </div>
                </div> -->

                <div class="form-group">
                    <label for="clover-merchant-id"
                           class="control-label">
                        {{ l('CLOVER_SETTINGS.MERCHANT_ID') }}
                    </label>
                    <div class="form-h__right fixedwidth">
                        <input id="clover-merchant-id"
                               v-model="companyCloverConfig.merchantID"
                               type="text"
                               disabled="disabled"
                               class="form-control input-lg">
                        <p class="help-block">
                            {{ l('CLOVER_SETTINGS.COMPANY_WIDE_SETTING') }}
                        </p>
                    </div>
                </div>

                <div v-if="showUtilityButtons"
                     class="form-group">
                    <label class="control-label">{{ l('CLOVER_SETTINGS.CHECK_STATUS') }}</label>
                    <div class="form-h__right fixedwidth">
                        <div class="clover-utility-buttons">
                            <SquareBtn size="lg"
                                       class="btn-info"
                                       :loading="pinging"
                                       @click="ping()">
                                {{ l('CLOVER_SETTINGS.PING') }}
                            </SquareBtn>

                            <SquareBtn size="lg"
                                       class="btn-info"
                                       :loading="checkingStatus"
                                       @click="status()">
                                {{ l('CLOVER_SETTINGS.STATUS') }}
                            </SquareBtn>

                            <SquareBtn size="lg"
                                       class="btn-info"
                                       :loading="cancellingInFlight"
                                       @click="confirmCancelInFlight()">
                                {{ l('CLOVER_SETTINGS.CANCEL_IN_FLIGHT') }}
                            </SquareBtn>
                        </div>
                        <div v-if="deviceStateMessage && !checkingStatus && !pinging"
                             class="alert mt-1"
                             :class="deviceStateMessage.alertClass">
                            <i :class="deviceStateMessage.icon"></i>
                            {{ deviceStateMessage.message }}
                            <small>{{ dateFilter(new Date(), true) }}</small>
                        </div>
                    </div>
                </div>
            </form>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <button class="btn btn-lg btn-default cancel-btn"
                    @click.prevent="$emit('cancel')">
                {{ l('CANCEL') }}
            </button>

            <!-- OK button -->
            <button class="btn btn-lg btn-primary"
                    :disabled="saving || configError "
                    @click.prevent="submit">
                <i v-if="saving"
                   class="fa-solid fa-spinner fa-spin" />
                <template v-else>
                    {{ l('SAVE') }}
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import CloverConfig from '@/lib/PaymentMethods/Clover/CloverConfig';
import CloverStatusResponse from '@/lib/PaymentMethods/Clover/Enums/CloverStatusResponse';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import SquareBtn from '@/vue/components/SquareBtn';
import { apiResponseToError } from '@/lib/Api';
import { container } from '@/container';
import { dateFilter } from '@/vue/filters/dateFilter';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { removeEmptyValues } from '@/functions/objects';

/**
 * This uses some non-translated strings because it's really for internal use only.
 */
export default {
    name: 'CloverSettingsTab',

    components: { SquareBtn },

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            checkingStatus: false,
            deviceStateMessage: null,
            pinging: false,
            saving: false,
            cancellingInFlight: false,
            configError: null,
            companyCloverConfig: null,
            // **This is the device config only**.
            // Other settings might exist on the company payment method.
            // If a value exists in the company config and is omitted from the device config,
            // the company level setting will be used.
            deviceCloverConfig: {
                xCloverDeviceId: null,
                xCloverTimeout: null,
                authBearerToken: null,
            },
            device: null,
            cloverConfig: null,
            paymentMethodRef: PaymentMethodRef.CARD_CLOVER_CHIP_AND_PIN,
            showUtilityButtons: false,
        };
    },

    created() {
        this.cloverConfig = new CloverConfig();

        /**
         * @type {!Device}
         */
        this.device = container.currentDevice.device;

        // Load the payment method.
        const paymentMethod = container.paymentMethodService.getByRef(this.paymentMethodRef);
        if (!paymentMethod) {
            // Throw an error if the payment method is not enabled (how did we get here though?)
            this.configError = container.langService.getText('CLOVER_SETTINGS.ERROR_CLOVER_DISABLED');
        }

        // Set the existing company-level config in the Vue data.
        // This is for reference in the UI only and will not be modified here.
        let rawCompanyCloverConfig = paymentMethod.config ? JSON.parse(paymentMethod.config) : {};
        // Clean the company config in case empty keys have been set in Backoffice.
        removeEmptyValues(rawCompanyCloverConfig);

        try {
            if (rawCompanyCloverConfig.merchants) {
                this.companyCloverConfig = rawCompanyCloverConfig.merchants.find(
                    (m) => m.branchIDs.find((branchID) => parseInt(branchID, 10) === container.userService.getOrderBranchID())
                );
            }
        } catch (e) {}

        if (!this.companyCloverConfig) {
            this.configError = container.langService.getText('CLOVER_SETTINGS.ERROR_NO_CLOVER_BRANCH');
            return;
        }

        // Copy any existing values from the device's paymentTerminalConfig.clover object
        // to the new config (this.deviceCloverConfig).
        if (this.device.paymentTerminalConfig?.[this.paymentMethodRef]) {
            Object.entries(this.device.paymentTerminalConfig[this.paymentMethodRef])
                .forEach(([key, value]) => {
                    if (value || value === false) {
                        this.deviceCloverConfig[key] = value;
                    }
                });
        }

        this.validateConfig();

        if (!this.configError && this.deviceCloverConfig.xCloverDeviceId) {
            this.showUtilityButtons = true;
        }
    },

    methods: {
        dateFilter,
        async submit() {
            if (this.saving) {
                return;
            }

            this.saving = true;
            this.configError = null;

            // Validate the config.
            const {
                newDeviceConfig,
                configInstance,
            } = this.validateConfig();

            if (this.configError) {
                this.saving = false;
                return;
            }

            // Send the new settings to the API to be saved on the device.
            // Remember, we only want to update the Clover settings not anything else that is stored in the config,
            // so the new value starts as the existing value, and we only replace one key.
            let newDevicePaymentTerminalConfig = this.device?.paymentTerminalConfig;

            // In case there is no existing JSON in the paymentTerminalConfig.
            if (!newDevicePaymentTerminalConfig) {
                newDevicePaymentTerminalConfig = {};
            }
            // In case there is no existing Clover key in the paymentTerminalConfig.
            if (!newDevicePaymentTerminalConfig.hasOwnProperty(this.paymentMethodRef)) {
                newDevicePaymentTerminalConfig[this.paymentMethodRef] = {};
            }
            newDevicePaymentTerminalConfig[this.paymentMethodRef] = newDeviceConfig;

            const newDevicePaymentTerminalConfigJson = JSON.stringify(newDevicePaymentTerminalConfig);

            container.deviceResource.setPaymentTerminalConfig(
                {
                    deviceID: this.device.ID,
                },
                {
                    paymentTerminalConfig: newDevicePaymentTerminalConfigJson,
                },
                () => {
                    // Set the new values on the current device so they are populated if the settings modal is reopened,
                    // without having to reload the app.
                    /**
                     * @type {CurrentDevice}
                     */
                    const currentDevice = container.currentDevice;
                    // Convert it back from JSON instead of using the object so that it is a clone and doesn't
                    // contain anything like Vue's reactivity.
                    currentDevice.device.paymentTerminalConfig = JSON.parse(newDevicePaymentTerminalConfigJson);

                    // Save the device to storage.
                    currentDevice.cache();

                    // Update the config in the driver, so we can make an API request to set terminalID if necessary.
                    configInstance.username = String(currentDevice.ID);
                    container.cloverDriver.config = configInstance;

                    this.saving = false;

                    if (!configInstance.xCloverDeviceId) {
                        this.configError = container.langService.getText('CLOVER_SETTINGS.SELECT_TERMINAL');
                        return;
                    }

                    // Because we've already updated the config in the driver we don't need to tell the user to sync.
                    container.toastFactory.success(
                        'CLOVER_SETTINGS.SUCCESS_TOAST_TITLE'
                    );

                    this.$emit('resolve');
                },
                (result) => {
                    this.configError = apiResponseToError(result).message;
                    this.saving = false;
                }
            );
        },

        /**
         * @return {{configInstance: CloverConfig, newDeviceConfig: {}}}
         */
        validateConfig() {
            // TODO refactor this move into function that we can use in Dojo too.
            // TODO can we extend this so we have company, branch, device, hard-coded?
            // Build a new config object containing just the device config we want to save.
            const newDeviceConfig = {};
            // Build a new combined company + device config object from the form data so that we can validate it.
            const newCombinedConfig = {};
            Object.keys(this.companyCloverConfig).forEach((key) => {
                if (this.companyCloverConfig[key] || this.companyCloverConfig[key] === false) {
                    newCombinedConfig[key] = this.companyCloverConfig[key];
                }
            });

            Object.keys(this.deviceCloverConfig).forEach((key) => {
                if (this.deviceCloverConfig[key] || this.deviceCloverConfig[key] === false) {
                    newCombinedConfig[key] = this.deviceCloverConfig[key];
                    newDeviceConfig[key] = this.deviceCloverConfig[key];
                }
            });

            // Clean the combined values (e.g. no keys that are just spaces).
            removeEmptyValues(newCombinedConfig);
            console.log('Validating new Clover config.', newCombinedConfig);

            const configInstance = new CloverConfig();

            // Validate the new config.
            try {
                container.hydrator.hydrate(newCombinedConfig, configInstance);
                configInstance.validate();
                configInstance.configAvailableForCurrentBranch = true;
            } catch (e) {
                this.configError = e.message;
            }

            return {
                newDeviceConfig,
                configInstance,
            };
        },

        async showTerminals() {
            container.vueModalFactory.open(
                MODAL_NAMES__EPOS.CLOVER_TERMINALS,
                {
                    xCloverDeviceId: this.deviceCloverConfig.xCloverDeviceId || null,
                }
            ).then((xCloverDeviceId) => {
                if (xCloverDeviceId) {
                    // Clear existing errors.
                    this.configError = null;
                    this.deviceCloverConfig.xCloverDeviceId = xCloverDeviceId;
                }
            }).catch(() => {
                // The user cancelled
            });
        },

        async ping() {
            try {
                container.cloverDriver.init();
            } catch (error) {
                this.configError = error.message;
                return;
            }
            this.pinging = true;
            container.cloverDriver.ping()
                .then(() => {
                    this.pinging = false;
                    this.setDeviceStateMessage(
                        'Connected',
                        'fa-solid fa-wifi text-success',
                        'alert-success'
                    );
                }).catch((error) => {
                    this.pinging = false;
                    this.setDeviceStateMessage(
                        error,
                        'fa-solid fa-circle-xmark text-danger',
                        'alert-danger'
                    );
                });
        },

        async status() {
            try {
                container.cloverDriver.init();
            } catch (error) {
                this.configError = error.message;
                return;
            }
            this.checkingStatus = true;
            container.cloverDriver.status()
                .then((data) => {
                    this.checkingStatus = false;
                    let icon = 'fa-solid fa-circle-xmark text-danger';
                    let alertClass = 'alert-danger';
                    switch (data) {
                        case CloverStatusResponse.IDLE:
                            icon = 'fa-solid fa-circle-check text-success';
                            alertClass = 'alert-success';
                            break;
                        case CloverStatusResponse.BUSY:
                        case CloverStatusResponse.WAITING_FOR_CUSTOMER:
                            icon = 'fa-solid fa-circle-check text-warning';
                            alertClass = 'alert-warning';
                            break;
                        case CloverStatusResponse.UNKNOWN:
                        default:
                    }
                    this.setDeviceStateMessage(
                        data,
                        icon,
                        alertClass
                    );
                }).catch((error) => {
                    this.checkingStatus = false;
                    this.setDeviceStateMessage(
                        error,
                        'fa-solid fa-circle-xmark text-danger',
                        'alert-danger'
                    );
                });
        },

        async confirmCancelInFlight() {
            container.dialogService
                .confirm('CLOVER_SETTINGS.CONFIRM_CANCEL_IN_FLIGHT')
                .then((confirmed) => {
                    if (confirmed) {
                        this.cancelInFlight();
                    }
                    // The user cancelled
                    return null;
                });
        },

        async cancelInFlight() {
            try {
                container.cloverDriver.init();
            } catch (error) {
                this.configError = error.message;
                return;
            }

            this.cancellingInFlight = true;
            this.deviceStateMessage = null;

            container.cloverDriver.cancelInFlight()
                .then(() => {
                    this.cancellingInFlight = false;
                    this.status();
                }).catch((error) => {
                    this.cancellingInFlight = false;
                    this.setDeviceStateMessage(
                        error,
                        'fa-solid fa-circle-xmark text-danger',
                        'alert-danger'
                    );
                });
        },

        setDeviceStateMessage(message, icon, alertClass) {
            this.deviceStateMessage = {
                message,
                icon,
                alertClass,
            };
        },
    },
};
</script>

<style scoped lang="less">
    @import "../../../../less/variables.less";

    .form-h__right {
        flex-wrap: wrap;
    }
    @media (max-width: @mobile-max) {
        .help-block {
            flex-basis: 100%;
        }
    }

    .clover-utility-buttons {
        display: flex;
        justify-content: flex-start;
        grid-gap: 15px;

        button:last-of-type {
            margin-left: auto;
        }
    }
    @media (max-width: @mobile-max) {
        .clover-utility-buttons {
            flex-direction: column;

            button:last-of-type {
                margin-left: initial;
            }
        }
    }
</style>
